// useUserAccess.tsx
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

export type UserRole =
  | "viewer"
  | "scheduler"
  | "accountant"
  | "schedule_admin"
  | "accounting_admin"
  | "admin"
  | "super_admin";


export const USER_ROLE_LEVEL : Record<UserRole, number> ={
	'viewer': 1,
	'scheduler': 2,
	'accountant': 3,
	'schedule_admin': 4,
	'accounting_admin': 5,
	'admin': 6,
	'super_admin': 7,
}
export const USER_ROLES = Object.keys(USER_ROLE_LEVEL) as UserRole[];

interface UserAccess {
	hasAdminAccess: boolean;
	hasAccAccess: boolean;
	userEmail: string;
	accessToDeals: string[];
	userRole: UserRole;
	isLoading: boolean;
}

const UserAccessContext = createContext<UserAccess>({
	hasAdminAccess: false,
	hasAccAccess: false,
	userEmail: '',
	accessToDeals: [],
	userRole: 'viewer',
	isLoading: false,
});

export const UserAccessProvider = ({ children }: { children: ReactNode }) => {
	const [hasAdminAccess, setHasAdminAccess] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [hasAccAccess, setHasAccAccess] = useState(false);
	const [userEmail, setUserEmail] = useState('');
	const [accessToDeals, setAccessToDeals] = useState<string[]>([]);
	const [userRole, setUserRole] = useState<UserRole>('viewer');

	useEffect(() => {
		setIsLoading(true);
		const auth = getAuth();
		const db = getFirestore();
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user) {
				setUserEmail(user.email || '');
				const userDoc = await getDoc(doc(db, 'users', user.uid));
				if (userDoc.exists()) {
					const userData = userDoc.data();
					const role = userData.role as UserRole;
					// Sets type of user access
					setHasAccAccess(role === 'accounting_admin' || role === 'admin' || role === 'accountant' || role === 'super_admin' || userData.canAccessTriggers === true);
					setHasAdminAccess(role === 'admin' || role === 'super_admin' || userData.canAccessTriggers === true);
					setUserRole(role || 'viewer');

					// Set accessToDeals - default to empty array if field doesn't exist
					const dealsAccess = userData.accessToDeals;
					if (Array.isArray(dealsAccess)) {
					  	setAccessToDeals(dealsAccess);
					} else {
					  	setAccessToDeals([]); // Empty array will mean access to all
					}
				}
			} else {
				setHasAdminAccess(false);
				setHasAccAccess(false);
				setUserEmail('');
				setAccessToDeals([]);
			}
			setIsLoading(false);
		});

		return () => unsubscribe();
	}, []);

	return (

		<UserAccessContext.Provider value={{ 
			hasAdminAccess,
			isLoading: false,
			hasAccAccess,
			userEmail,
			userRole,
			accessToDeals,
		}}>
			{children}
		</UserAccessContext.Provider>
	);
};

export const useUserAccess = () => useContext(UserAccessContext);
